/**
 * NotificationsPopup
 *
 * Renders a tabbed notifications view (All, Archived) inside a scrollable menu.
 * Fetches lists for the active user, shows an unread badge, and supports
 * "Mark all as read". Uses OverlayScrollbars for consistent scroll styling.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Tabs, Tab, Box, Typography, Badge } from "@mui/material";
import AllNotifications from "./notification-tabs/all-notifications/all-notifications";
import ArchivedNotifications from "./notification-tabs/archived-notifications/archived-notifications";
import "./notifications.scss";

import {
  getArchivedNoticationsByUserID,
  getNoticationsByUserID,
  markAllAsRead,
} from "../../api/notification";
import { formatDistanceToNow } from "date-fns";
import { getCurrentUser } from "../../common/routines/user-operations";
import { useSetLoading } from "../../common/hooks/appearance";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

type TabType = "all" | "mentions" | "archived";

interface NotificationsPopupProps {
  onClose: () => void;
  setNotificationCountUpdate: any;
}

const NotificationsPopup: React.FC<NotificationsPopupProps> = ({
  onClose,
  setNotificationCountUpdate,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [loading, setLoading] = useState(false);
  const [unreadCountUpdate, setUnreadCountUpdate] = useState(false);
  const [notifications, setNotifications] = useState<any>({
    all: [],
    archived: [],
  });
  const unreadCount = useMemo(() => {
    const unreadInAll = notifications.all.filter((n: any) => !n.isRead).length;
    const unreadInArchived = notifications.archived.filter(
      (n: any) => !n.isRead,
    ).length;
    return unreadInAll + unreadInArchived;
  }, [notifications]);

  useEffect(() => {
    setUnreadCountUpdate(unreadCount);
    setNotificationCountUpdate(unreadCount);
  }, [unreadCount]);

  const [userId, setUserId] = useState<number | null>(null);
  const userDetails = getCurrentUser();

  useEffect(() => {
    if (userDetails) {
      setUserId(userDetails.employeeId);
    }
  }, [userDetails]);

  const fetchAllNotifications = async () => {
    setLoading(true);
    try {
      const allNotifications = await getNoticationsByUserID(userId);
      const allTransformed = allNotifications.data.data.map((item: any) => ({
        id: item.notificationId,
        user: item.senderName?.employeeFullName,
        message: item.message,
        detail: item.eventDescription?.triggerEvent || "",
        referenceTitle: item.eventDescription?.referenceTitle || "",
        time: item.createdAt
          ? (() => {
              const ts = item.createdAt.toString().replace("UTC ", "");
              const date = new Date(ts.endsWith("Z") ? ts : ts + "Z");
              return Number.isNaN(date.getTime())
                ? "Invalid date"
                : formatDistanceToNow(date, { addSuffix: true });
            })()
          : "Unknown time",
        icon: item.senderName?.employeeImage,
        isRead: item.isRead,
        moduleName: item.moduleName,
        respectiveId: item.respectivePk,
        moduleId: item?.moduleId,
      }));

      setNotifications((prev: any) => ({
        ...prev,
        all: allTransformed,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedNotifications = async () => {
    setLoading(true);
    try {
      const archivedNotifications =
        await getArchivedNoticationsByUserID(userId);
      const archivedTransformed = archivedNotifications.data.data.map(
        (item: any) => ({
          id: item.notificationId,
          user: item.senderName?.employeeFullName,
          message: item.message,
          detail: item.eventDescription?.triggerEvent || "",
          referenceTitle: item.eventDescription?.referenceTitle || "",
          time: item.createdAt
            ? (() => {
                const ts = item.createdAt.toString().replace("UTC ", "");
                const date = new Date(ts.endsWith("Z") ? ts : ts + "Z");
                return Number.isNaN(date.getTime())
                  ? "Invalid date"
                  : formatDistanceToNow(date, { addSuffix: true });
              })()
            : "Unknown time",
          icon: item.senderName?.employeeImage,
          isRead: item.isRead,
          moduleName: item?.moduleName,
          respectiveId: item?.respectivePk,
          moduleId: item?.moduleId,
        }),
      );
      setNotifications((prev: any) => ({
        ...prev,
        archived: archivedTransformed,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchAllNotifications();
      fetchArchivedNotifications();
    }
  }, [userId]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabType) => {
    setActiveTab(newValue);
    if (newValue === "all") {
      fetchAllNotifications();
    } else if (newValue === "archived") {
      fetchArchivedNotifications();
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead(userId);
      fetchAllNotifications();
    } catch (error) {
      console.error(error);
    }
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <OverlayScrollbarsComponent
      className="notifications-popup"
      options={{
        scrollbars: {
          theme: "os-theme-dark",
          autoHide: "scroll",
          autoHideDelay: 400,
        },
      }}
    >
      <div className="header">
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#141414",
            fontSize: "16px",
            fontWeight: "600 !important",
            fontFamily: "Manrope, sans-serif",
          }}
        >
          Notifications
          <Badge
            badgeContent={unreadCountUpdate}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "#F15B59", // red circle
                color: "#fff !important", // white number
                fontSize: "0.70rem",
                height: "24px", // fixed height
                width: "24px", // fixed width (same as height)
                borderRadius: "50%", // circle
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0, // remove extra padding
                lineHeight: 1,
                paddingBottom: "1px",
              },
            }}
          />
        </Typography>
        <button
          className="mark-read"
          style={{ display: "flex", alignItems: "center", gap: "8px" }}
          onClick={handleMarkAllAsRead}
        >
          Mark all as read
        </button>
      </div>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
        slotProps={{
          indicator: {
            children: <span className="MuiTabs-indicatorSpan" />,
          },
        }}
      >
        <Tab label="All" value="all" />
        <Tab label="Archived" value="archived" />
      </Tabs>

      <Box className="tab-content">
        {activeTab === "all" && (
          <AllNotifications
            data={notifications.all}
            fetchNotifications={fetchAllNotifications}
            onClose={onClose}
          />
        )}
        {activeTab === "archived" && (
          <ArchivedNotifications
            data={notifications.archived}
            onClose={onClose}
          />
        )}
      </Box>
    </OverlayScrollbarsComponent>
  );
};

export default NotificationsPopup;
